import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
// FLOW[49428]: production typography is bundled locally, so visual QA and ERP UI
// do not depend on third-party font CDNs or host network availability.
import '@fontsource/onest/300.css';
import '@fontsource/onest/400.css';
import '@fontsource/onest/500.css';
import '@fontsource/onest/600.css';
import '@fontsource/onest/700.css';
import '@fontsource/cormorant-garamond/600.css';
import '@fontsource/cormorant-garamond/700.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/600.css';
import App from './App';
import './index.css';
import './styles/print.css';
// FLOW[24142]: MS205 - Sentry initialization on app startup
import { initMonitoring } from './utils/monitoring';

async function enableMocking() {
  // FLOW[23330]: Only enable MSW when explicitly requested via VITE_USE_MOCKS=true
  // By default, use real backend APIs for integration testing
  if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS === 'true') {
    const { worker } = await import('./api/mocks/browser');
    return worker.start({
      onUnhandledRequest: 'bypass',
    });
  }
  return Promise.resolve();
}

async function registerFreshServiceWorker() {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  try {
    // FLOW[50331]: an already open ERP tab adopts a deployed release itself;
    // operators never have to discover that a manual browser refresh is needed.
    const hadController = Boolean(navigator.serviceWorker.controller);
    let reloadStarted = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadController || reloadStarted) return;
      reloadStarted = true;
      window.location.reload();
    });
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none',
    });
    await registration.update();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void registration.update().catch((error) => {
          console.warn('[SW] Fresh-release visibility check failed', error);
        });
      }
    });
  } catch (error) {
    // The ERP remains fully usable online if SW registration is unavailable.
    console.warn('[SW] Fresh-release registration failed', error);
  }
}


async function initializeApp() {
  // Initialize monitoring (Sentry/LogRocket) if DSN is provided
  await initMonitoring({
    sentry: {
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.MODE as 'development' | 'staging' | 'production',
      release: import.meta.env.VITE_APP_VERSION || '1.0.0',
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
      enabled: Boolean(import.meta.env.VITE_SENTRY_DSN),
    },
    logRocket: {
      appId: import.meta.env.VITE_LOGROCKET_APP_ID,
      enabled: Boolean(import.meta.env.VITE_LOGROCKET_APP_ID),
    },
  });

  // Enable MSW mocking if configured
  await enableMocking();

  // Render the app
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>
  );
  void registerFreshServiceWorker();
}

initializeApp();
